import type { Tour } from './tours'
import { driver } from 'driver.js'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { useBranding } from '~/helpers/branding/branding-context'
import { useAuth } from '~/helpers/store/auth'
import { anchorSelector } from './anchors'
import { TOURS, toursForRole } from './tours'
import { waitForElement } from './wait-for-element'
import 'driver.js/dist/driver.css'

type IProps = {
  tours?: Tour[]
  /** Délai laissé à l'écran pour afficher sa première ancre (données chargées). */
  timeoutMs?: number
}

/**
 * Démarre une visite guidée demandée par l'URL (`?tour=<id>`) (#78).
 *
 * Le centre d'aide navigue vers l'écran avec ce paramètre ; ce composant, monté
 * une fois dans `Root`, attend que la première ancre soit là — l'écran charge
 * encore ses données — puis lance les bulles. Le paramètre est retiré dans tous
 * les cas : recharger la page ne relance pas la visite.
 */
function TourLauncher({ tours = TOURS, timeoutMs = 8000 }: IProps) {
  const { t } = useTranslation()
  const role = useAuth(state => state.payload?.role)
  const capabilities = useAuth(state => state.capabilities)
  const { billingSelfService, loaded } = useBranding()
  const [params, setParams] = useSearchParams()
  const tourId = params.get('tour')

  useEffect(() => {
    if (!tourId) return
    const clearParam = () => setParams((current) => {
      current.delete('tour')
      return current
    }, { replace: true })

    // Visite de la facturation (#165) : tant que la configuration n'a pas dit si
    // l'achat en ligne est ouvert, on ne tranche pas — l'écarter retirerait le
    // paramètre, et la visite serait perdue pour de bon.
    if (!loaded && tours.some(candidate => candidate.id === tourId && candidate.selfServiceBilling)) return

    const tour = toursForRole(role, tours, capabilities, { billingSelfService }).find(candidate => candidate.id === tourId)
    const first = tour?.steps[0]
    if (!tour || !first) {
      clearParam()
      return
    }

    let cancelled = false
    void waitForElement(anchorSelector(first.anchor), timeoutMs).then((element) => {
      if (cancelled) return
      clearParam()
      if (!element) {
        toast.error(t('tour.unavailable'))
        return
      }
      driver({
        showProgress: true,
        skipMissingElement: true,
        nextBtnText: t('tour.next'),
        prevBtnText: t('tour.prev'),
        doneBtnText: t('tour.done'),
        // driver.js remplace lui-même `{{current}}` et `{{total}}`.
        progressText: t('tour.progress', { current: '{{current}}', total: '{{total}}' }),
        popoverClass: 'studeva-tour',
        // driver.js nomme son bouton de fermeture « Close », en anglais et en dur (RGAA 8.7).
        onPopoverRender: (popover) => {
          popover.closeButton.setAttribute('aria-label', t('common.close'))
          popover.closeButton.setAttribute('title', t('common.close'))
        },
        steps: tour.steps.map(step => ({
          element: anchorSelector(step.anchor),
          popover: { title: t(step.titleKey), description: t(step.bodyKey) },
        })),
      }).drive()
    })

    return () => {
      cancelled = true
    }
  }, [tourId, role, capabilities, billingSelfService, loaded, tours, timeoutMs, setParams, t])

  return null
}

export default TourLauncher
